import { useCallback, useState } from 'react';

const SESSION_STORAGE_KEY = 'stopover_skip_external_link_modal';

export type UseExternalLinkModalReturn = {
  isModalVisible: boolean;
  skipModal: boolean;
  pendingUrl: string | null;
  handleCardButtonClick: (url: string) => void;
  handleConfirm: () => void;
  handleCancel: () => void;
  handleSkipModalChange: (value: boolean) => void;
};

const getSkipPreference = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const setSkipPreference = (value: boolean): void => {
  try {
    if (value) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // sessionStorage not available – silently ignore
  }
};

export const useExternalLinkModal = (): UseExternalLinkModalReturn => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [skipModal, setSkipModal] = useState(false);

  const handleCardButtonClick = useCallback((url: string) => {
    if (getSkipPreference()) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setPendingUrl(url);
    setSkipModal(false);
    setIsModalVisible(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (skipModal) {
      setSkipPreference(true);
    }
    setIsModalVisible(false);
    if (pendingUrl) {
      window.open(pendingUrl, '_blank', 'noopener,noreferrer');
    }
    setPendingUrl(null);
  }, [pendingUrl, skipModal]);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
    setPendingUrl(null);
    setSkipModal(false);
  }, []);

  const handleSkipModalChange = useCallback((value: boolean) => {
    setSkipModal(value);
  }, []);

  return {
    isModalVisible,
    skipModal,
    pendingUrl,
    handleCardButtonClick,
    handleConfirm,
    handleCancel,
    handleSkipModalChange,
  };
};
